import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faSpinner,
  faLayerGroup,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';
import { orderApi } from '../api/orderApi';

const STRATEGIES = [
  { id: 'custom', label: 'Custom (2–4 legs)' },
  { id: 'call_debit', label: 'Call debit spread' },
  { id: 'call_credit', label: 'Call credit spread' },
  { id: 'put_debit', label: 'Put debit spread' },
  { id: 'put_credit', label: 'Put credit spread' },
  { id: 'straddle', label: 'Long straddle' },
  { id: 'strangle', label: 'Long strangle' },
  { id: 'iron_condor', label: 'Iron condor' },
];

const INTENTS = [
  { value: 'buy_to_open', label: 'BTO', side: 'buy' },
  { value: 'sell_to_open', label: 'STO', side: 'sell' },
  { value: 'buy_to_close', label: 'BTC', side: 'buy' },
  { value: 'sell_to_close', label: 'STC', side: 'sell' },
];

const emptyLeg = () => ({
  symbol: '',
  side: 'buy',
  positionIntent: 'buy_to_open',
  ratioQty: 1,
  strike: null,
  type: null,
  mid: null,
});

function pickNearest(rows, target) {
  if (!rows?.length) return null;
  if (!Number.isFinite(target)) return rows[Math.floor(rows.length / 2)] || rows[0];
  return [...rows].sort((a, b) => Math.abs(a.strike - target) - Math.abs(b.strike - target))[0];
}

function formatPx(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(2);
}

function estimateNetDebit(legs) {
  // Rough mid estimate: buy legs add mid, sell legs subtract mid
  let net = 0;
  let used = 0;
  for (const leg of legs) {
    if (leg.mid == null || !Number.isFinite(Number(leg.mid))) continue;
    const ratio = Number(leg.ratioQty) || 1;
    const signed = leg.side === 'buy' ? 1 : -1;
    net += signed * Number(leg.mid) * ratio;
    used += 1;
  }
  return used ? net : null;
}

const MultiLegOrderForm = ({ symbol: initialSymbol = '', onOrderSubmitted }) => {
  const [underlying, setUnderlying] = useState(String(initialSymbol || 'AAPL').toUpperCase());
  const [strategy, setStrategy] = useState('call_debit');
  const [expiry, setExpiry] = useState('');
  const [expiries, setExpiries] = useState([]);
  const [calls, setCalls] = useState([]);
  const [puts, setPuts] = useState([]);
  const [legs, setLegs] = useState([emptyLeg(), emptyLeg()]);
  const [qty, setQty] = useState('1');
  const [orderType, setOrderType] = useState('limit');
  const [limitPrice, setLimitPrice] = useState('');
  const [loadingChain, setLoadingChain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [chainMessage, setChainMessage] = useState('');
  const [pickerType, setPickerType] = useState('call');

  useEffect(() => {
    if (initialSymbol) setUnderlying(String(initialSymbol).toUpperCase());
  }, [initialSymbol]);

  const loadChain = useCallback(async (sym, exp) => {
    const symbol = String(sym || '').trim().toUpperCase();
    if (!symbol) return;
    try {
      setLoadingChain(true);
      setError('');
      setChainMessage('');
      const data = await marketDataApi.getOptionChain(symbol, exp ? { expiry: exp } : {});
      if (data.available === false) {
        setChainMessage(data.message || 'Options chain unavailable on this account');
        setExpiries(data.expiries || []);
        setCalls([]);
        setPuts([]);
        return;
      }
      setExpiries(data.expiries || []);
      setExpiry(data.expiry || '');
      setCalls(data.calls || []);
      setPuts(data.puts || []);
      if (!data.calls?.length && !data.puts?.length) {
        setChainMessage(data.message || 'No contracts for this expiry');
      }
    } catch (err) {
      console.error('Option chain error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load option chain');
      setCalls([]);
      setPuts([]);
    } finally {
      setLoadingChain(false);
    }
  }, []);

  useEffect(() => {
    loadChain(underlying, null);
  }, [underlying, loadChain]);

  const applyStrategyTemplate = useCallback(
    (strategyId, nextCalls = calls, nextPuts = puts) => {
      const midCall = nextCalls[Math.floor(nextCalls.length / 2)];
      const midPut = nextPuts[Math.floor(nextPuts.length / 2)];
      const atmStrike = midCall?.strike ?? midPut?.strike;
      const width = Math.max(1, Math.round((atmStrike || 100) * 0.02));

      const asLeg = (contract, intent) => {
        if (!contract) return emptyLeg();
        const meta = INTENTS.find((i) => i.value === intent) || INTENTS[0];
        return {
          symbol: contract.contract,
          side: meta.side,
          positionIntent: intent,
          ratioQty: 1,
          strike: contract.strike,
          type: contract.type,
          mid: contract.mid,
        };
      };

      let nextLegs = [emptyLeg(), emptyLeg()];

      if (strategyId === 'call_debit') {
        const buy = pickNearest(nextCalls, atmStrike);
        const sell = pickNearest(nextCalls, (buy?.strike || atmStrike) + width);
        nextLegs = [asLeg(buy, 'buy_to_open'), asLeg(sell, 'sell_to_open')];
      } else if (strategyId === 'call_credit') {
        const sell = pickNearest(nextCalls, atmStrike);
        const buy = pickNearest(nextCalls, (sell?.strike || atmStrike) + width);
        nextLegs = [asLeg(sell, 'sell_to_open'), asLeg(buy, 'buy_to_open')];
      } else if (strategyId === 'put_debit') {
        const buy = pickNearest(nextPuts, atmStrike);
        const sell = pickNearest(nextPuts, (buy?.strike || atmStrike) - width);
        nextLegs = [asLeg(buy, 'buy_to_open'), asLeg(sell, 'sell_to_open')];
      } else if (strategyId === 'put_credit') {
        const sell = pickNearest(nextPuts, atmStrike);
        const buy = pickNearest(nextPuts, (sell?.strike || atmStrike) - width);
        nextLegs = [asLeg(sell, 'sell_to_open'), asLeg(buy, 'buy_to_open')];
      } else if (strategyId === 'straddle') {
        const call = pickNearest(nextCalls, atmStrike);
        const put = pickNearest(nextPuts, call?.strike ?? atmStrike);
        nextLegs = [asLeg(call, 'buy_to_open'), asLeg(put, 'buy_to_open')];
      } else if (strategyId === 'strangle') {
        const call = pickNearest(nextCalls, (atmStrike || 0) + width);
        const put = pickNearest(nextPuts, (atmStrike || 0) - width);
        nextLegs = [asLeg(call, 'buy_to_open'), asLeg(put, 'buy_to_open')];
      } else if (strategyId === 'iron_condor') {
        const putBuy = pickNearest(nextPuts, (atmStrike || 0) - width * 2);
        const putSell = pickNearest(nextPuts, (atmStrike || 0) - width);
        const callSell = pickNearest(nextCalls, (atmStrike || 0) + width);
        const callBuy = pickNearest(nextCalls, (atmStrike || 0) + width * 2);
        nextLegs = [
          asLeg(putBuy, 'buy_to_open'),
          asLeg(putSell, 'sell_to_open'),
          asLeg(callSell, 'sell_to_open'),
          asLeg(callBuy, 'buy_to_open'),
        ];
      } else {
        nextLegs = legs.length >= 2 ? legs : [emptyLeg(), emptyLeg()];
      }

      setLegs(nextLegs);
      const est = estimateNetDebit(nextLegs);
      if (est != null) setLimitPrice(est.toFixed(2));
    },
    [calls, puts, legs]
  );

  useEffect(() => {
    if ((calls.length || puts.length) && strategy !== 'custom') {
      applyStrategyTemplate(strategy, calls, puts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, puts]);

  const estimated = useMemo(() => estimateNetDebit(legs), [legs]);

  const updateLeg = (index, patch) => {
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)));
  };

  const setLegIntent = (index, intent) => {
    const meta = INTENTS.find((i) => i.value === intent) || INTENTS[0];
    updateLeg(index, { positionIntent: intent, side: meta.side });
  };

  const addLegFromContract = (contract) => {
    if (!contract?.contract) return;
    setLegs((prev) => {
      if (prev.length >= 4) return prev;
      const blankIdx = prev.findIndex((l) => !l.symbol);
      const next = {
        symbol: contract.contract,
        side: 'buy',
        positionIntent: 'buy_to_open',
        ratioQty: 1,
        strike: contract.strike,
        type: contract.type,
        mid: contract.mid,
      };
      if (blankIdx >= 0) {
        const copy = [...prev];
        copy[blankIdx] = next;
        return copy;
      }
      return [...prev, next];
    });
    setStrategy('custom');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const filled = legs.filter((l) => l.symbol);
    if (filled.length < 2 || filled.length > 4) {
      setError('Add 2–4 option legs before submitting');
      return;
    }
    if (!qty || Number(qty) <= 0) {
      setError('Enter strategy quantity (number of spreads/units)');
      return;
    }
    if (orderType === 'limit' && (limitPrice === '' || Number.isNaN(Number(limitPrice)))) {
      setError('Enter a net limit price (debit positive, credit negative)');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        qty: Number(qty),
        type: orderType,
        timeInForce: 'day',
        legs: filled.map((l) => ({
          symbol: l.symbol,
          side: l.side,
          positionIntent: l.positionIntent,
          ratioQty: Number(l.ratioQty) || 1,
        })),
      };
      if (orderType === 'limit') payload.limitPrice = Number(limitPrice);

      const result = await orderApi.submitMultiLegOrder(payload);
      setSuccess(`Submitted ${result.data?.id || 'mleg order'} (${result.data?.status || 'ok'})`);
      onOrderSubmitted?.(result.data);
    } catch (err) {
      console.error('Multi-leg submit error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to submit multi-leg order');
    } finally {
      setSubmitting(false);
    }
  };

  const pickerRows = pickerType === 'put' ? puts : calls;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[10px] text-muted">
          Underlying
          <input
            value={underlying}
            onChange={(e) => setUnderlying(e.target.value.toUpperCase())}
            onBlur={() => loadChain(underlying, null)}
            className="mt-0.5 block h-8 w-24 rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
          />
        </label>
        <label className="text-[10px] text-muted">
          Strategy
          <select
            value={strategy}
            onChange={(e) => {
              const id = e.target.value;
              setStrategy(id);
              if (id !== 'custom') applyStrategyTemplate(id);
            }}
            className="mt-0.5 block h-8 min-w-[160px] rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
          >
            {STRATEGIES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="text-[10px] text-muted">
          Expiry
          <select
            value={expiry}
            onChange={(e) => {
              const exp = e.target.value;
              setExpiry(exp);
              loadChain(underlying, exp);
            }}
            className="mt-0.5 block h-8 min-w-[120px] rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
          >
            {expiries.map((exp) => (
              <option key={exp} value={exp}>{exp}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => loadChain(underlying, expiry || null)}
          className="h-8 px-2.5 rounded-md border border-hairline text-[11px] text-muted hover:bg-elevated inline-flex items-center gap-1"
          disabled={loadingChain}
        >
          <FontAwesomeIcon icon={loadingChain ? faSpinner : faRotate} className={loadingChain ? 'animate-spin' : ''} />
          Chain
        </button>
        <label className="text-[10px] text-muted">
          Qty (units)
          <input
            type="number"
            min="1"
            step="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="mt-0.5 block h-8 w-20 rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
          />
        </label>
        <label className="text-[10px] text-muted">
          Type
          <select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            className="mt-0.5 block h-8 rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
          >
            <option value="limit">Limit</option>
            <option value="market">Market</option>
          </select>
        </label>
        {orderType === 'limit' && (
          <label className="text-[10px] text-muted">
            Net limit (+debit / −credit)
            <input
              type="number"
              step="0.01"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              placeholder={estimated != null ? estimated.toFixed(2) : '0.50'}
              className="mt-0.5 block h-8 w-36 rounded-md border border-hairline bg-canvas px-2 text-xs text-ink"
            />
          </label>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="h-8 px-3 rounded-md bg-[var(--lt-action)] text-white text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faLayerGroup} />}
          Submit mleg
        </button>
      </div>

      {(error || success || chainMessage) && (
        <div className="text-[11px] space-y-1">
          {error ? <div className="text-[var(--lt-bear)]">{error}</div> : null}
          {success ? <div className="text-[var(--lt-bull)]">{success}</div> : null}
          {chainMessage ? <div className="text-muted">{chainMessage}</div> : null}
          {estimated != null ? (
            <div className="text-muted">
              Est. net mid: <span className="font-semibold text-ink">{estimated.toFixed(2)}</span>
              {' '}({estimated >= 0 ? 'debit' : 'credit'})
            </div>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-md border border-hairline overflow-hidden">
          <div className="px-2 py-1.5 border-b border-hairline flex items-center justify-between gap-2 bg-elevated/40">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">Legs</span>
            <button
              type="button"
              disabled={legs.length >= 4}
              onClick={() => {
                setStrategy('custom');
                setLegs((prev) => [...prev, emptyLeg()]);
              }}
              className="h-6 px-1.5 rounded text-[10px] text-[var(--lt-action)] hover:bg-elevated disabled:opacity-40 inline-flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
              Add leg
            </button>
          </div>
          <div className="divide-y divide-hairline">
            {legs.map((leg, index) => (
              <div key={`leg-${index}`} className="p-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="w-4 text-muted tabular-nums">{index + 1}</span>
                <select
                  value={leg.positionIntent}
                  onChange={(e) => setLegIntent(index, e.target.value)}
                  className="h-7 rounded border border-hairline bg-canvas px-1.5 text-[10px]"
                >
                  {INTENTS.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
                <input
                  value={leg.symbol}
                  onChange={(e) => updateLeg(index, { symbol: e.target.value.toUpperCase() })}
                  placeholder="OCC symbol"
                  className="h-7 flex-1 min-w-[140px] rounded border border-hairline bg-canvas px-1.5 font-mono text-[10px]"
                />
                <input
                  type="number"
                  min="1"
                  value={leg.ratioQty}
                  onChange={(e) => updateLeg(index, { ratioQty: e.target.value })}
                  title="Ratio qty"
                  className="h-7 w-12 rounded border border-hairline bg-canvas px-1 text-[10px]"
                />
                <span className="text-muted w-14 text-right tabular-nums">{formatPx(leg.mid)}</span>
                <button
                  type="button"
                  disabled={legs.length <= 2}
                  onClick={() => setLegs((prev) => prev.filter((_, i) => i !== index))}
                  className="h-7 w-7 rounded text-[var(--lt-bear)] hover:bg-elevated disabled:opacity-30"
                  title="Remove leg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-hairline overflow-hidden">
          <div className="px-2 py-1.5 border-b border-hairline flex items-center gap-2 bg-elevated/40">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">Chain</span>
            <div className="inline-flex rounded border border-hairline overflow-hidden text-[10px]">
              <button
                type="button"
                onClick={() => setPickerType('call')}
                className={`px-2 py-0.5 ${pickerType === 'call' ? 'bg-[var(--lt-action)] text-white' : 'text-muted hover:bg-elevated'}`}
              >
                Calls
              </button>
              <button
                type="button"
                onClick={() => setPickerType('put')}
                className={`px-2 py-0.5 ${pickerType === 'put' ? 'bg-[var(--lt-action)] text-white' : 'text-muted hover:bg-elevated'}`}
              >
                Puts
              </button>
            </div>
            <span className="ml-auto text-[10px] text-muted">{pickerRows.length} contracts</span>
          </div>
          <div className="max-h-44 overflow-y-auto">
            {loadingChain ? (
              <div className="py-8 text-center text-muted text-[11px]">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                Loading chain…
              </div>
            ) : pickerRows.length === 0 ? (
              <div className="py-8 text-center text-muted text-[11px]">No contracts</div>
            ) : (
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-muted">
                    <th className="px-2 py-1">Strike</th>
                    <th className="px-2 py-1">Bid</th>
                    <th className="px-2 py-1">Ask</th>
                    <th className="px-2 py-1">Mid</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {pickerRows.map((row) => (
                    <tr key={row.contract} className="border-t border-hairline hover:bg-elevated/50">
                      <td className="px-2 py-1 tabular-nums font-medium">{formatPx(row.strike)}</td>
                      <td className="px-2 py-1 tabular-nums">{formatPx(row.bid)}</td>
                      <td className="px-2 py-1 tabular-nums">{formatPx(row.ask)}</td>
                      <td className="px-2 py-1 tabular-nums">{formatPx(row.mid)}</td>
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          onClick={() => addLegFromContract(row)}
                          className="px-1.5 py-0.5 rounded text-[var(--lt-action)] hover:bg-elevated"
                          title={row.contract}
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default MultiLegOrderForm;
